import React, { useState } from 'react';
import { useQueryClient } from 'react-query';

import styled from 'styled-components';

import { createAIFeedback, deleteAIFeedback, updateAIFeedback } from 'apis/FeedbackAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Modal from 'components/lib/Modal';
import TextArea from 'components/lib/TextArea';

import { AI_FEEDBACK_TYPES, AIFeedbackFeatureType, AIFeedbackType } from './constants';

const Subtitle = styled.div`
  font-family: var(--font-regular);
  font-size: 14px;
  color: var(--text-secondary);
`;

const Body = styled.div`
  padding: 20px 0 25px;
`;

const Label = styled.div`
  font-size: 14px;
  margin-bottom: 5px;
`;

const StyledButton = styled(Button)`
  padding-left: 30px;
  padding-right: 30px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const RemoveLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--active-blue);
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
`;

type Props = {
  mode: AIFeedbackType;
  subtitle: string;
  featureType: AIFeedbackFeatureType;
  recommendationOverview?: number | null;
  actionPlan?: number | null;
  cmiDigestRunCommunity?: number | null;
  community?: number | null;
  sourceUrl?: string;
  existingFeedback?: any;
  onClose: () => void;
};

const FeedbackModal = ({
  mode,
  subtitle,
  featureType,
  recommendationOverview,
  actionPlan,
  cmiDigestRunCommunity,
  community,
  sourceUrl,
  existingFeedback,
  onClose,
}: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const initialComment = existingFeedback?.comment ?? '';
  const [comment, setComment] = useState(initialComment);
  const [loading, setLoading] = useState(false);
  const isHelpful = mode === AI_FEEDBACK_TYPES.HELPFUL;
  const submitDisabled = loading || (!isHelpful && comment.trim().length === 0);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (existingFeedback) {
        await updateAIFeedback(existingFeedback.id, { feedbackType: mode, comment });
      } else {
        await createAIFeedback({
          featureType,
          feedbackType: mode,
          comment,
          recommendationOverview,
          actionPlan,
          cmiDigestRunCommunity,
          community,
          sourceUrl,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['ai-feedbacks'] });
      showMessage('success', existingFeedback ? 'Feedback updated.' : 'Feedback submitted.');
      onClose();
    } catch (err) {
      showMessage('error', existingFeedback ? 'Failed to update feedback.' : 'Failed to submit feedback.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!existingFeedback) return;
    setLoading(true);
    try {
      await deleteAIFeedback(existingFeedback.id);
      await queryClient.invalidateQueries({ queryKey: ['ai-feedbacks'] });
      showMessage('success', 'Feedback removed.');
      onClose();
    } catch (err) {
      showMessage('error', 'Failed to remove feedback.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      centered
      destroyOnClose
      className="compact bare-header"
      title={
        <div>
          <div>{isHelpful ? 'This is Helpful' : 'This is Not Helpful'}</div>
          <Subtitle>{subtitle}</Subtitle>
        </div>
      }
      onCancel={onClose}
      footer={
        <Footer>
          {existingFeedback && (
            <RemoveLink type="button" disabled={loading} onClick={handleRemove}>
              Remove Feedback
            </RemoveLink>
          )}
          <Actions>
            <StyledButton
              key="submit"
              type="primary"
              loading={loading}
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              Submit Feedback
            </StyledButton>
            <StyledButton key="cancel" disabled={loading} onClick={onClose}>
              Cancel
            </StyledButton>
          </Actions>
        </Footer>
      }
    >
      <Body>
        <Label>{isHelpful ? 'Comment (Optional)' : 'Comment'}</Label>
        <TextArea
          rows={4}
          placeholder={isHelpful ? 'What did you find useful?' : 'What could be improved?'}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Body>
    </Modal>
  );
};

export default FeedbackModal;
